import React, { useState, useRef } from 'react';
import { Music, Check, CheckCircle2, X, AlertCircle, Loader2 } from 'lucide-react';
import apiService from '../../../services/api';
import './VoiceUploadForm.css';

const VoiceUploadForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({ name: '', description: '', gender: 'unknown', audio: null });
  const [currentStep, setCurrentStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [uploadedFileKey, setUploadedFileKey] = useState('');
  const [createdVoiceName, setCreatedVoiceName] = useState('');
  const fileInputRef = useRef(null);

  const steps = [
    { num: 1, label: '上传音频' },
    { num: 2, label: '设置信息' },
    { num: 3, label: '克隆完成' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      setError('请选择音频文件 (MP3、WAV、M4A)');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('音频文件大小不能超过 50MB');
      return;
    }
    if (file.size < 10 * 1024) {
      setError('音频文件太小，请选择有效的音频文件');
      return;
    }
    setFormData(prev => ({ ...prev, audio: file }));
    setError('');
    setUploadProgress(0);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect({ target: { files: [file] } });
  };

  const handleDragOver = (e) => e.preventDefault();

  // Preview-mode mock: simulate upload progress then succeed
  const mockUpload = () => {
    setUploading(true);
    setError('');
    setUploadProgress(0);
    let pct = 0;
    const timer = setInterval(() => {
      pct += Math.floor(Math.random() * 18) + 8;
      if (pct >= 100) {
        pct = 100;
        clearInterval(timer);
        setUploadProgress(100);
        setUploadedFileKey('mock-audio-key-preview');
        setTimeout(() => {
          setUploading(false);
          setCurrentStep(2);
        }, 600);
      } else {
        setUploadProgress(pct);
      }
    }, 200);
  };

  const handleAudioUpload = async () => {
    if (!formData.audio) {
      setError('请选择音频文件');
      return;
    }

    // Preview mode: skip real upload
    if (process.env.REACT_APP_UI_PREVIEW === 'true') {
      mockUpload();
      return;
    }

    try {
      setUploading(true);
      setError('');
      setUploadProgress(0);

      const uploadData = new FormData();
      uploadData.append('file', formData.audio);

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) { clearInterval(progressInterval); return prev; }
          return prev + Math.random() * 12;
        });
      }, 400);

      const result = await apiService.uploadAudio(uploadData);
      clearInterval(progressInterval);

      if (result.fileKey) {
        setUploadedFileKey(result.fileKey);
        setUploadProgress(100);
        setTimeout(() => {
          setUploading(false);
          setCurrentStep(2);
        }, 600);
      } else {
        throw new Error('服务器响应格式错误');
      }
    } catch (err) {
      setError('上传失败：' + err.message);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCreateVoice = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { setError('请输入声音名称'); return; }
    if (!uploadedFileKey) { setError('请先上传音频文件'); return; }

    // Preview mode: skip real API call
    if (process.env.REACT_APP_UI_PREVIEW === 'true') {
      setProcessing(true);
      setError('');
      const previewName = formData.name;
      setTimeout(() => {
        setProcessing(false);
        setCreatedVoiceName(previewName);
        setCurrentStep(3);
      }, 1200);
      return;
    }

    try {
      setProcessing(true);
      setError('');
      await apiService.createAudioClone({
        name: formData.name,
        description: formData.description,
        gender: formData.gender,
        fileKey: uploadedFileKey,
      });
      setCreatedVoiceName(formData.name);
      setCurrentStep(3);
      setFormData({ name: '', description: '', gender: 'unknown', audio: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadedFileKey('');
    } catch (err) {
      setError('克隆失败：' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const resetToStep1 = () => {
    setCurrentStep(1);
    setFormData({ name: '', description: '', gender: 'unknown', audio: null });
    setUploadedFileKey('');
    setCreatedVoiceName('');
    setError('');
    setUploadProgress(0);
  };

  const handleGoMyVoices = () => {
    if (onSuccess) onSuccess();
  };

  return (
    <div className="vuf-wrap">

      {/* Step indicators */}
      <div className="vuf-steps">
        {steps.map((step, index) => (
          <React.Fragment key={step.num}>
            <div className="vuf-step">
              <div className={`vuf-step-circle ${currentStep > step.num ? 'done' : currentStep === step.num ? 'active' : ''}`}>
                {currentStep > step.num ? <Check size={13} strokeWidth={2.5} /> : step.num}
              </div>
              <span className={`vuf-step-label ${currentStep >= step.num ? 'active' : ''}`}>{step.label}</span>
            </div>
            {index < steps.length - 1 && (
              <div className={`vuf-step-line ${currentStep > step.num ? 'done' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Upload audio */}
      {currentStep === 1 && (
        <div className="vuf-card">
          <h2 className="vuf-card-title">上传训练音频</h2>
          <p className="vuf-card-desc">上传清晰的音频文件，用于克隆您的专属声音</p>

          <div
            className={`vuf-dropzone${formData.audio ? ' has-file' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => !formData.audio && fileInputRef.current?.click()}
          >
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="audio/*" className="vuf-file-input" />

            {formData.audio ? (
              <div className="vuf-file-info">
                <div className="vuf-file-icon"><Music size={20} /></div>
                <div className="vuf-file-details">
                  <div className="vuf-file-name">{formData.audio.name}</div>
                  <div className="vuf-file-size">{formatFileSize(formData.audio.size)}</div>
                </div>
                <button
                  className="vuf-file-remove"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFormData(p => ({ ...p, audio: null }));
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="vuf-dropzone-empty">
                <div className="vuf-upload-icon"><Music size={22} /></div>
                <p className="vuf-upload-title">拖拽音频文件到此处，或点击选择</p>
                <p className="vuf-upload-hint">支持 MP3、WAV、M4A 格式，文件大小不超过 50MB</p>
              </div>
            )}
          </div>

          {uploading && (
            <div className="vuf-progress">
              <div className="vuf-progress-header">
                <Loader2 size={13} className="vuf-spinner" />
                <span>正在上传音频...</span>
                <span className="vuf-progress-pct">{Math.round(uploadProgress)}%</span>
              </div>
              <div className="vuf-progress-track">
                <div className="vuf-progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
              <div className="vuf-progress-meta">
                <span>{formData.audio?.name}</span>
                <span>{uploadProgress < 100 ? '上传中...' : '处理中...'}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="vuf-error">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          <div className="vuf-actions">
            <button
              type="button"
              className="vuf-btn vuf-btn-primary"
              onClick={handleAudioUpload}
              disabled={!formData.audio || uploading}
            >
              {uploading
                ? <><Loader2 size={13} className="vuf-spinner" /> 上传中...</>
                : '上传音频'
              }
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Set info */}
      {currentStep === 2 && (
        <form className="vuf-card" onSubmit={handleCreateVoice}>
          <h2 className="vuf-card-title">设置声音信息</h2>
          <p className="vuf-card-desc">填写声音基本信息，开始克隆</p>

          <div className="vuf-notice-success">
            <CheckCircle2 size={16} />
            <div>
              <div className="vuf-notice-title">音频上传成功</div>
              <div className="vuf-notice-desc">现在请填写声音信息来完成克隆</div>
            </div>
          </div>

          <div className="vuf-form-group">
            <label className="vuf-label" htmlFor="vuf-name">
              声音名称 <span className="vuf-required">*</span>
            </label>
            <input
              className="vuf-input"
              id="vuf-name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="请输入声音名称"
            />
          </div>

          <div className="vuf-form-group">
            <label className="vuf-label" htmlFor="vuf-gender">性别</label>
            <select
              className="vuf-select"
              id="vuf-gender"
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
            >
              <option value="unknown">未知</option>
              <option value="male">男性</option>
              <option value="female">女性</option>
            </select>
          </div>

          <div className="vuf-form-group">
            <label className="vuf-label" htmlFor="vuf-description">描述</label>
            <textarea
              className="vuf-textarea"
              id="vuf-description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="请输入声音描述（可选）"
              rows={3}
            />
          </div>

          {processing && (
            <div className="vuf-processing">
              <Loader2 size={15} className="vuf-spinner" />
              <div>
                <div className="vuf-processing-title">正在克隆声音...</div>
                <div className="vuf-processing-desc">这可能需要几分钟时间，请耐心等待</div>
              </div>
            </div>
          )}

          {error && (
            <div className="vuf-error">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          <div className="vuf-actions">
            <button
              type="submit"
              className="vuf-btn vuf-btn-primary"
              disabled={processing || !formData.name.trim()}
            >
              {processing
                ? <><Loader2 size={13} className="vuf-spinner" /> 克隆中...</>
                : '开始克隆'
              }
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Success */}
      {currentStep === 3 && (
        <div className="vuf-card vuf-success-card">
          <div className="vuf-success-icon"><CheckCircle2 size={28} strokeWidth={1.5} /></div>
          <h2 className="vuf-success-title">声音克隆成功</h2>
          <p className="vuf-success-desc">
            「{createdVoiceName}」已成功克隆，现在可以用于视频生成了
          </p>
          <div className="vuf-actions">
            <button type="button" className="vuf-btn vuf-btn-ghost" onClick={resetToStep1}>
              克隆另一个
            </button>
            <button type="button" className="vuf-btn vuf-btn-primary" onClick={handleGoMyVoices}>
              查看我的声音
            </button>
          </div>
        </div>
      )}

      {/* Guidelines */}
      <div className="vuf-guidelines">
        <h3 className="vuf-guidelines-title">录音要求</h3>
        <ul className="vuf-guidelines-list">
          <li>音频时长建议在 1 到 10 分钟之间</li>
          <li>音质清晰，无明显噪音或回声</li>
          <li>语速适中，发音清晰自然</li>
          <li>建议使用单人录音，避免多人混录</li>
          <li>避免背景音乐和环境杂音</li>
          <li>内容丰富，涵盖不同语调和情感</li>
        </ul>
      </div>

    </div>
  );
};

export default VoiceUploadForm;
